import {
  CUSTOM_RV_TYPE_SORT,
  inTransitDefaultImageUrl,
  INVENTORY_IMAGE_WIDTH,
  noPhotoDefaultImageUrl,
  RANGE_FILTER_SECTIONS,
  SLEEPING_CAPACITY_EQUIPMENT_PREFIX,
  SLEEPS_SEARCH_PARAM_KEY,
  SORT_BY_SECTION,
} from '@/constants/inventory';
import {
  AdvertisedPriceField,
  AdvertisedPriceStatus,
  BaseFilters,
  FilterPromotion,
  FilterPromotionInventoryField,
  Filters,
  FilterSection,
  FilterValue,
  IAdvertisedPriceFields,
  InventoryType,
  ISectionOption,
  RangeFilterValue,
  SortBy,
  TagFilters,
} from '@/types/inventory';
import { ILayoutContextData } from '@/types/layout';
import {
  eastCoastStates,
  getFacetCode,
  getInventoryPageUrl,
  getMultipleFilter,
  getRangeFilter,
  getSingleFilter,
  westCoastStates,
} from '@lamesarv-sdk/tools';
import {
  IBasicInventory,
  IBody,
  IInventory,
  IInventoryRebate,
  IMinimalInventory,
  InventoryField,
  InventoryFieldMap,
} from '@lamesarv-sdk/types';
import { getCldImageUrl, GetCldImageUrlOptions } from 'next-cloudinary';

export const getDefaultSort = (context: ILayoutContextData, customGeoLocation?: [number, number]) => {
  const sortList = [`${InventoryField.order}:asc`];

  if (customGeoLocation)
    sortList.unshift(`${InventoryField.geoLocation}(${customGeoLocation[0]},${customGeoLocation[1]}):asc`);

  if (context.geoLocation)
    sortList.unshift(`${InventoryField.geoLocation}(${context.geoLocation[0]},${context.geoLocation[1]}):asc`);

  return sortList.slice(0, 2).join(',');
};

export const isNewUnit = (item: { [InventoryField.inventoryType] }) =>
  item[InventoryField.inventoryType].toLowerCase() === 'new';

export const getListPriceLabel = (item: IBasicInventory): string => {
  return isNewUnit(item) ? 'MSRP' : 'Was';
};

/*
  Pricing rules live in the importer (dbo.TsGetAdvertisedPrice), the site only renders its result:
    OK           advertisedPrice is the fee-inclusive price
    NO_FEE_ROW   advertisedPrice is the display-on-web price, without fees
    CROSSED_OUT  advertisedPrice is shown crossed out
    NO_PRICE     nothing to show
*/
export const getAdvertisedPriceStatus = (item: IMinimalInventory & IAdvertisedPriceFields) =>
  item[AdvertisedPriceField.status] ?? AdvertisedPriceStatus.noPrice;

export const isAdvertisedPriceFinal = (item: IMinimalInventory & IAdvertisedPriceFields) => {
  const status = getAdvertisedPriceStatus(item);
  return status === AdvertisedPriceStatus.ok || status === AdvertisedPriceStatus.noFeeRow;
};

export const getUnlockPriceLabel = (item: IBasicInventory) => {
  if (item[InventoryField.isSold]) {
    return {
      buttonLabel: 'Shop Similar RVs',
    };
  }

  const isFinalPrice = isAdvertisedPriceFinal(item);

  return {
    buttonLabel: isFinalPrice ? "Lock in Today's Price" : "Unlock Today's Price",
    subtext: isFinalPrice
      ? "We'll get back to you right away with today's best pricing."
      : "We'll get back to you right away.",
  };
};

export const getImageUrl = (
  src: string,
  width: number,
  height?: number,
  extraOptions?: Partial<GetCldImageUrlOptions>,
) =>
  getCldImageUrl({
    src,
    width,
    height,
    ...(extraOptions || {}),
  });

export const isInStock = (item: IBasicInventory) => !!item[InventoryField.daysInStock];

export const getDefaultImageUrl = (item: IBasicInventory) => {
  if (isInStock(item)) return noPhotoDefaultImageUrl;

  if (item[InventoryField.defaultImageUrl]) return item[InventoryField.defaultImageUrl];

  return inTransitDefaultImageUrl;
};

export const getUniqueInventoryImageUrl = (item: IBasicInventory, customImage?: string, useCloudinaryIds = false) => {
  if (customImage) return customImage;

  if (useCloudinaryIds && item[InventoryField.imageCloudinaryIds]?.length)
    return getImageUrl(item[InventoryField.imageCloudinaryIds][0], INVENTORY_IMAGE_WIDTH);

  if (item[InventoryField.thumbnails]?.length) return item[InventoryField.thumbnails][0];
  if (item[InventoryField.images]?.length) return item[InventoryField.images][0];

  return getDefaultImageUrl(item);
};

type FilterSectionConverter = (value: FilterValue, baseFilters: BaseFilters) => string | null;

const getDefaultFilterSectionConverter =
  (field: string): FilterSectionConverter =>
  (value) => {
    if (!value) return null;

    if (!(value instanceof Array)) return getRangeFilter(field, value.min, value.max);

    if (value.length === 0) return null;

    return getMultipleFilter(field, value, { useLiteralValue: true });
  };

const getUniqueFilterSectionConverter =
  (field: InventoryField): FilterSectionConverter =>
  (value, baseFilters) => {
    if (!value || !(value instanceof Array) || value.length === 0) return null;

    const facetValuesMap = new Map<string, string[]>();

    for (const option of value) {
      const facetValue = getFacetCode(option);

      facetValuesMap.set(facetValue, []);
    }

    const baseValues = baseFilters[field] || [];

    for (const baseValue of baseValues) {
      const facetValue = getFacetCode(baseValue);

      if (facetValuesMap.has(facetValue)) {
        facetValuesMap.get(facetValue).push(baseValue);
      }
    }

    return getMultipleFilter(field, Array.from(facetValuesMap.values()).flat(), { useLiteralValue: true });
  };

export const customFilterSectionConverter: Record<FilterSection, FilterSectionConverter> = {
  [FilterSection.MAKE]: getUniqueFilterSectionConverter(InventoryField.make),
  [FilterSection.MODEL]: getUniqueFilterSectionConverter(InventoryField.model),
  [FilterSection.FLOOR_PLAN]: getUniqueFilterSectionConverter(InventoryField.modelNumber),
  [FilterSection.FEATURES]: getUniqueFilterSectionConverter(InventoryField.equipment),

  [FilterSection.RV_TYPE]: getDefaultFilterSectionConverter(InventoryField.body),
  [FilterSection.LOCATION]: getDefaultFilterSectionConverter(InventoryField.location),
  [FilterSection.FUEL_TYPE]: getDefaultFilterSectionConverter(InventoryField.fuelType),
  [FilterSection.SLIDEOUTS]: getDefaultFilterSectionConverter(InventoryField.slideOuts),
  [FilterSection.LENGTH]: getDefaultFilterSectionConverter(InventoryField.length),
  [FilterSection.PRICE]: getDefaultFilterSectionConverter(InventoryField.price),
  [FilterSection.YEAR]: getDefaultFilterSectionConverter(InventoryField.modelYear),
  [FilterSection.SLEEPS]: (value) => {
    if (!value || value instanceof Array) return null;

    const sleepingCapacityEquipments = Array.from({ length: value.max - value.min + 1 }).map(
      (_, index) => `${SLEEPING_CAPACITY_EQUIPMENT_PREFIX}${index + value.min}`,
    );

    return getMultipleFilter(InventoryField.equipment, sleepingCapacityEquipments, { useLiteralValue: true });
  },
  [FilterSection.CONDITION]: (value) => {
    if (!(value instanceof Array) || value.length === 0) return null;

    return getMultipleFilter(InventoryField.inventoryType, value, { useLiteralValue: true });
  },
  [FilterSection.PROMOTIONS]: (value) => {
    if (!(value instanceof Array) || value.length === 0) return null;

    return `(${value.map((promotion) => getSingleFilter(FilterPromotionInventoryField[promotion], 'true')).join(' || ')})`;
  },
};

export const convertTagFilter = (section: string, tagFilters: TagFilters) => {
  const tagValue = tagFilters[section] || [];

  if (tagValue.length === 0) return null;

  return getMultipleFilter(
    InventoryField.customTags,
    tagValue.map((v) => `${section}:${v}`),
    { useLiteralValue: true },
  );
};

export const convertFilterSection = (filters: Filters, baseFilters: BaseFilters, tagFilters: TagFilters) => {
  const filtersArr: string[] = [];

  for (const section of Object.values(FilterSection)) {
    const value = filters[section];
    const converter = customFilterSectionConverter[section];

    if (!converter) continue;

    const sectionFilter = hasFilters(value) ? converter(value, baseFilters) : null;
    const tagFilter = convertTagFilter(section, tagFilters);

    let filter = sectionFilter || tagFilter;
    if (sectionFilter && tagFilter) {
      filter = `(${sectionFilter} || ${tagFilter})`;
    }

    if (filter) filtersArr.push(filter);
  }

  return filtersArr.join(' && ');
};

/** Converts a tag category name to its search param key, e.g. "Drive Train" -> "driveTrain". */
export const getSearchParamKey = (name: string): string => {
  return name
    .split(' ')
    .map((word, index) => (index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');
};

export const formatPriceFilter = (value?: number) =>
  value !== undefined ? (Math.floor(value / 100) / 10).toFixed(1) + 'K' : '';

const getSortedUniqueOptions = (options?: string[]) => {
  if (!options || options.length === 0) return [];

  const addedFacets = new Set<string>();
  const result: string[] = [];
  for (const option of options) {
    const facetOption = getFacetCode(option);

    if (addedFacets.has(facetOption)) continue;

    addedFacets.add(facetOption);
    result.push(option);
  }

  return result.toSorted((a, b) => a.localeCompare(b));
};
export interface SectionOptionsGetterParams {
  baseFilters: BaseFilters;
  filters: Filters;
  modelsByMake: Record<string, string[]>;
  equipmentFilters: string[];
}

const sectionOptionsGetterMap: Record<string, (data: SectionOptionsGetterParams) => ISectionOption[]> = {
  [FilterSection.PROMOTIONS]: () => [],
  [FilterSection.CONDITION]: ({ baseFilters }) => baseFilters[InventoryField.inventoryType],
  [FilterSection.RV_TYPE]: ({ baseFilters }) => {
    const options = baseFilters[InventoryField.body] || [];

    return options.toSorted((a, b) => {
      const aIndex = CUSTOM_RV_TYPE_SORT.indexOf(a) ?? CUSTOM_RV_TYPE_SORT.length;
      const bIndex = CUSTOM_RV_TYPE_SORT.indexOf(b) ?? CUSTOM_RV_TYPE_SORT.length;

      return aIndex - bIndex;
    });
  },
  [FilterSection.MAKE]: ({ baseFilters }) => getSortedUniqueOptions(baseFilters[InventoryField.make]),
  [FilterSection.FLOOR_PLAN]: ({ baseFilters }) => getSortedUniqueOptions(baseFilters[InventoryField.modelNumber]),
  [FilterSection.FEATURES]: ({ baseFilters, equipmentFilters }) => {
    const availableFacetCodes = new Set((baseFilters[InventoryField.equipment] || []).map(getFacetCode));

    const options = equipmentFilters.filter((option) => availableFacetCodes.has(getFacetCode(option)));

    return getSortedUniqueOptions(options);
  },
  [FilterSection.LOCATION]: ({ baseFilters }) => {
    const baseOptions = baseFilters[InventoryField.location] || [];

    const eastCoastOptions: string[] = [];
    const westCoastOptions: string[] = [];
    const options: ISectionOption[] = [];
    for (const option of baseOptions) {
      const stateAbbreviation = option.slice(-2).toUpperCase();

      if (eastCoastStates.has(stateAbbreviation)) {
        eastCoastOptions.push(option);
      } else if (westCoastStates.has(stateAbbreviation)) {
        westCoastOptions.push(option);
      } else {
        options.push(option);
      }
    }

    if (eastCoastOptions.length > 0) {
      options.push({
        label: 'East Coast',
        options: eastCoastOptions,
      });
    }

    if (westCoastOptions.length > 0) {
      options.push({
        label: 'West Coast',
        options: westCoastOptions,
      });
    }

    return options;
  },
  [FilterSection.FUEL_TYPE]: ({ baseFilters }) => baseFilters[InventoryField.fuelType],
  [FilterSection.SLIDEOUTS]: ({ baseFilters }) => baseFilters[InventoryField.slideOuts],
  [FilterSection.LENGTH]: ({ baseFilters }) =>
    baseFilters[InventoryField.length] && ['0', ...baseFilters[InventoryField.length]],
  [FilterSection.PRICE]: ({ baseFilters }) =>
    baseFilters[InventoryField.price] && ['0', ...baseFilters[InventoryField.price]],
  [FilterSection.YEAR]: ({ baseFilters }) =>
    baseFilters[InventoryField.modelYear] || ['2015', (new Date().getFullYear() + 1).toString()],
  [FilterSection.SLEEPS]: ({ baseFilters }) => {
    let maxSleepingCapacity = 10;

    for (const equipment of baseFilters[InventoryField.equipment] || []) {
      if (!equipment.startsWith(SLEEPING_CAPACITY_EQUIPMENT_PREFIX)) continue;

      const capacity = Number(equipment.replace(SLEEPING_CAPACITY_EQUIPMENT_PREFIX, '').trim());

      if (!isNaN(capacity) && capacity > maxSleepingCapacity) {
        maxSleepingCapacity = capacity;
      }
    }

    return ['1', maxSleepingCapacity.toString()];
  },
  // baseFilters[InventoryField.sleepingCapacity] && ['0', ...baseFilters[InventoryField.sleepingCapacity]],
  [FilterSection.MODEL]: ({ baseFilters, filters, modelsByMake }) => {
    const makeFilter = filters[FilterSection.MAKE];

    let baseOptions = baseFilters[InventoryField.model] || [];
    if (makeFilter && makeFilter instanceof Array && makeFilter.length > 0) {
      baseOptions = [];
      for (const make of makeFilter) {
        const models = modelsByMake[make] || [];
        baseOptions.push(...models);
      }
    }

    return getSortedUniqueOptions(baseOptions);
  },
  [SORT_BY_SECTION]: () => Object.values(SortBy),
};

export const rangeFormatters: Partial<
  Record<
    FilterSection,
    {
      input: (value: number) => string;
      label: (value: number) => string;
    }
  >
> = {
  [FilterSection.LENGTH]: {
    input: (value) => value.toString(),
    label: (value) => `${value} ft.`,
  },
  [FilterSection.PRICE]: {
    input: (value) => `$${value.toLocaleString('en-US')}`,
    label: (value) => `$${(value / 1000).toFixed(1)}K`,
  },
  [FilterSection.SLEEPS]: {
    input: (value) => value.toString(),
    label: (value) => `${value} sleeps`,
  },
};

export const makeGetSectionOptions =
  (params: SectionOptionsGetterParams) =>
  (section: string): ISectionOption[] => {
    const optionsGetter = sectionOptionsGetterMap[section];
    const values = optionsGetter?.(params) || [];

    return values;
  };

const sortByMap: Record<SortBy, string> = {
  [SortBy.NEWEST]: `${InventoryField.modelYear}:desc`,
  [SortBy.OLDEST]: `${InventoryField.modelYear}:asc`,
  [SortBy.RECOMMENDED]: `${InventoryField.order}:asc`,
  [SortBy.PRICE_LOW_TO_HIGH]: `${InventoryField.price}:asc`,
  [SortBy.PRICE_HIGH_TO_LOW]: `${InventoryField.price}:desc`,
};

export const convertSortBy = (value?: SortBy) => (value && sortByMap[value]) || sortByMap[SortBy.RECOMMENDED];
export const revertSortBy = (value?: string) => {
  if (!value) return SortBy.RECOMMENDED;
  const sortByEntry = Object.entries(sortByMap).find(([, v]) => v === value);
  return sortByEntry ? (sortByEntry[0] as SortBy) : SortBy.RECOMMENDED;
};

const extractRangeFromSearchParamValue = (value: string): RangeFilterValue | null => {
  if (!value) return null;

  const rangeValues = value.split(':').map((v) => parseInt(v));

  if (rangeValues.length !== 2 || isNaN(rangeValues[0]) || isNaN(rangeValues[1])) return null;

  return {
    max: rangeValues[1],
    min: rangeValues[0],
  };
};

export const sectionSearchParamsExtractorMap: Record<
  FilterSection,
  InventoryField | ((searchParams: URLSearchParams, baseFilters: BaseFilters) => FilterValue | null)
> = {
  [FilterSection.CONDITION]: (searchParams, baseFilters) => {
    const value = searchParams.get(InventoryFieldMap.get(InventoryField.inventoryType));

    if (!value) return null;

    const optionsByFacetCode = new Map<string, string>();
    const baseOptions = baseFilters[InventoryField.inventoryType] || [];

    for (const option of baseOptions) {
      const facetCode = getFacetCode(option);
      if (optionsByFacetCode.has(facetCode)) continue;
      optionsByFacetCode.set(facetCode, option);
    }

    const facetValues = value.split(',');

    const result: string[] = [];
    for (const facet of facetValues) {
      const facetCode = getFacetCode(facet);
      if (optionsByFacetCode.has(facetCode)) {
        result.push(optionsByFacetCode.get(facetCode));
      }
    }

    return result;
  },
  [FilterSection.RV_TYPE]: InventoryField.body,
  [FilterSection.MAKE]: InventoryField.make,
  [FilterSection.MODEL]: InventoryField.model,
  [FilterSection.FLOOR_PLAN]: InventoryField.modelNumber,
  [FilterSection.FEATURES]: InventoryField.equipment,
  [FilterSection.LOCATION]: InventoryField.location,
  [FilterSection.FUEL_TYPE]: InventoryField.fuelType,
  [FilterSection.SLIDEOUTS]: InventoryField.slideOuts,
  [FilterSection.LENGTH]: InventoryField.length,
  [FilterSection.PRICE]: InventoryField.price,
  [FilterSection.YEAR]: InventoryField.modelYear,
  [FilterSection.SLEEPS]: (searchParams) => extractRangeFromSearchParamValue(searchParams.get(SLEEPS_SEARCH_PARAM_KEY)),
  [FilterSection.PROMOTIONS]: (searchParams) => {
    const values: FilterPromotion[] = [];

    if (searchParams.has(InventoryFieldMap.get(InventoryField.isDailyDeal))) {
      values.push(FilterPromotion.DAILY_DEAL);
    }
    if (searchParams.has(InventoryFieldMap.get(InventoryField.isFlashSale))) {
      values.push(FilterPromotion.FLASH_SALE);
    }

    if (values.length === 0) return null;

    return values;
  },
};

const defaultExtractor = (
  section: FilterSection,
  field: InventoryField,
  baseFilters: BaseFilters,
  searchParams: URLSearchParams,
): FilterValue | null => {
  const key = InventoryFieldMap.get(field);
  const value = searchParams.get(key);

  if (!value) return null;

  if (RANGE_FILTER_SECTIONS.has(section)) return extractRangeFromSearchParamValue(value);

  const optionsByFacetCodes = new Map<string, string>();

  const baseOptions = baseFilters[field] || [];

  for (const option of baseOptions) {
    const facetCode = getFacetCode(option);

    if (optionsByFacetCodes.has(facetCode)) continue;

    optionsByFacetCodes.set(facetCode, option);
  }

  const facets = value.split(',');
  const result: string[] = [];

  for (const facet of facets) {
    const facetCode = getFacetCode(facet);

    if (optionsByFacetCodes.has(facetCode)) {
      result.push(optionsByFacetCodes.get(facetCode));
    }
  }

  if (result.length === 0) return null;

  return result;
};

export const getFiltersFromSearchParams = (searchParams: URLSearchParams, baseFilters: BaseFilters) => {
  const filters: Filters = {};

  for (const [section, extractor] of Object.entries(sectionSearchParamsExtractorMap)) {
    const value =
      typeof extractor === 'function'
        ? extractor(searchParams, baseFilters)
        : defaultExtractor(section as FilterSection, extractor, baseFilters, searchParams);

    if (!value) continue;

    filters[section as FilterSection] = value;
  }

  return filters;
};

const filtersToSearchParams: Record<
  FilterSection,
  InventoryField | ((value: FilterValue) => { key: string; value: string }[])
> = {
  [FilterSection.CONDITION]: (value: FilterValue) => {
    if (!(value instanceof Array) || value.length === 0) return [];

    return [
      {
        key: InventoryFieldMap.get(InventoryField.inventoryType),
        value: value.map((v) => getFacetCode(v)).join(','),
      },
    ];
  },
  [FilterSection.RV_TYPE]: InventoryField.body,
  [FilterSection.MAKE]: InventoryField.make,
  [FilterSection.MODEL]: InventoryField.model,
  [FilterSection.FLOOR_PLAN]: InventoryField.modelNumber,
  [FilterSection.FEATURES]: InventoryField.equipment,
  [FilterSection.LOCATION]: InventoryField.location,
  [FilterSection.FUEL_TYPE]: InventoryField.fuelType,
  [FilterSection.SLIDEOUTS]: InventoryField.slideOuts,
  [FilterSection.LENGTH]: InventoryField.length,
  [FilterSection.PRICE]: InventoryField.price,
  [FilterSection.YEAR]: InventoryField.modelYear,
  [FilterSection.SLEEPS]: (value) => {
    if (!value || value instanceof Array) return [];

    return [
      {
        key: SLEEPS_SEARCH_PARAM_KEY,
        value: `${value.min}:${value.max}`,
      },
    ];
  },
  [FilterSection.PROMOTIONS]: (value: FilterValue) => {
    if (!(value instanceof Array) || value.length === 0) return [];

    return value.map((promotion) => ({
      key: InventoryFieldMap.get(FilterPromotionInventoryField[promotion]),
      value: 'true',
    }));
  },
};

export const convertFiltersToSearchParams = (filters: Filters) => {
  const searchParams = new URLSearchParams();

  for (const [section, value] of Object.entries(filters)) {
    const converter = filtersToSearchParams[section as FilterSection];

    if (!converter) continue;

    const converted =
      typeof converter === 'function'
        ? converter(value)
        : [
            {
              key: InventoryFieldMap.get(converter),
              value: value instanceof Array ? value.map(getFacetCode).join(',') : `${value.min}:${value.max}`,
            },
          ];

    for (const { key, value: val } of converted) {
      searchParams.set(key, val);
    }
  }

  return searchParams;
};

const FILTER_SECTION_SET = new Set(Object.values(FilterSection));
export const isFilterSection = (section: string): section is FilterSection => {
  return FILTER_SECTION_SET.has(section as FilterSection);
};

export const hasFilters = (value: FilterValue | undefined) => {
  if (!value) return false;

  if (!(value instanceof Array)) return true;

  return value.length > 0;
};

export const hasActiveRebate = (rebate?: IInventoryRebate | false): rebate is IInventoryRebate =>
  !!rebate && rebate.enddate > Date.now() / 1000;

export const getCleanDescription = (item: IBasicInventory): string => {
  const inventoryDescriptionReg = /<p>(.+?)<\/p>/m;
  return inventoryDescriptionReg.exec(item[InventoryField.description])?.[1] || '';
};

type IAdditionalProperty = {
  '@type': string;
  name: string;
  value: string | number;
};

const getSchemaAdditionalProperties = (item: IBasicInventory): IAdditionalProperty[] => {
  return (item[InventoryField.equipment] || []).map((equipmentItem: string) => ({
    '@type': 'PropertyValue',
    name: 'Equipment',
    value: equipmentItem,
  }));
};

export const getJsonLd = (item: IBasicInventory, publicUrl: string, useNewInventory0Mileage?: boolean) => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name: item[InventoryField.title],
    description: getCleanDescription(item) || undefined,
    image: getUniqueInventoryImageUrl(item),
    url: getInventoryPageUrl(item, publicUrl),
    bodyType: item[InventoryField.body],
    brand: {
      '@type': 'Brand',
      name: item[InventoryField.make],
    },
    model: item[InventoryField.model],
    modelDate: item[InventoryField.modelYear],
    vehicleIdentificationNumber: item[InventoryField.vinFull] || item[InventoryField.vin] || undefined,
    mileageFromOdometer: {
      '@type': 'QuantitativeValue',
      value:
        !item[InventoryField.mileage] ||
        (useNewInventory0Mileage && item[InventoryField.inventoryType] === InventoryType.new)
          ? 0
          : item[InventoryField.mileage],
      unitCode: 'SMI',
    },
    itemCondition:
      item[InventoryField.inventoryType] === InventoryType.new
        ? 'https://schema.org/NewCondition'
        : 'https://schema.org/UsedCondition',
    offers: {
      '@type': 'Offer',
      priceCurrency: item[InventoryField.price] ? 'USD' : undefined,
      price: item[InventoryField.price] || undefined,
      itemCondition:
        item[InventoryField.inventoryType] === InventoryType.new
          ? 'https://schema.org/NewCondition'
          : 'https://schema.org/UsedCondition',
      availability: item[InventoryField.isSold] ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      sku: item[InventoryField.stockNumber] || undefined,
    },
    additionalProperty: getSchemaAdditionalProperties(item) || undefined,
  };

  return jsonLd;
};

export const getBodyRoute = (page: IBody): string =>
  page.route || (page.filters ? `/search?${page.filters}` : `/search?body=${getFacetCode(page.title)}`);

export const getMinimalInventory = (
  inventoryItem: IInventory,
  defaultImageUrl: string,
  customImageUrl?: string,
): IMinimalInventory => {
  const basicInventoryItem: Partial<
    IMinimalInventory & IAdvertisedPriceFields & Pick<IInventory, InventoryField.price>
  > = {};
  const advertisedInventoryItem = inventoryItem as IInventory & IAdvertisedPriceFields;

  basicInventoryItem[InventoryField.body] = inventoryItem[InventoryField.body];
  basicInventoryItem[InventoryField.fuelType] = inventoryItem[InventoryField.fuelType];
  basicInventoryItem[InventoryField.idNoPrefix] = inventoryItem[InventoryField.idNoPrefix];
  basicInventoryItem[InventoryField.id] = inventoryItem[InventoryField.id];
  basicInventoryItem[InventoryField.isDailyDeal] = inventoryItem[InventoryField.isDailyDeal];
  basicInventoryItem[InventoryField.inventoryType] = inventoryItem[InventoryField.inventoryType];
  basicInventoryItem[InventoryField.length] = inventoryItem[InventoryField.length];
  basicInventoryItem[InventoryField.location] = inventoryItem[InventoryField.location];
  basicInventoryItem[InventoryField.make] = inventoryItem[InventoryField.make];
  basicInventoryItem[InventoryField.model] = inventoryItem[InventoryField.model];
  basicInventoryItem[InventoryField.order] = inventoryItem[InventoryField.order];
  basicInventoryItem[InventoryField.priceList] = inventoryItem[InventoryField.priceList];
  basicInventoryItem[InventoryField.priceSale] = inventoryItem[InventoryField.priceSale];
  basicInventoryItem[InventoryField.priceWas] = inventoryItem[InventoryField.priceWas];
  basicInventoryItem[InventoryField.rebate] = inventoryItem[InventoryField.rebate];
  basicInventoryItem[InventoryField.isFlashSale] = inventoryItem[InventoryField.isFlashSale];
  basicInventoryItem[InventoryField.slideOuts] = inventoryItem[InventoryField.slideOuts];
  basicInventoryItem[InventoryField.slideOutsCount] = inventoryItem[InventoryField.slideOutsCount];
  basicInventoryItem[InventoryField.stockNumber] = inventoryItem[InventoryField.stockNumber];
  basicInventoryItem[InventoryField.title] = inventoryItem[InventoryField.title];
  basicInventoryItem[InventoryField.isTooLowToShow] = inventoryItem[InventoryField.isTooLowToShow];
  basicInventoryItem[InventoryField.websitePrice] = inventoryItem[InventoryField.websitePrice];
  basicInventoryItem[InventoryField.priceFlag] = inventoryItem[InventoryField.priceFlag];
  basicInventoryItem[InventoryField.price] = inventoryItem[InventoryField.price];
  basicInventoryItem[AdvertisedPriceField.price] = advertisedInventoryItem[AdvertisedPriceField.price];
  basicInventoryItem[AdvertisedPriceField.status] = advertisedInventoryItem[AdvertisedPriceField.status];

  basicInventoryItem.image = getUniqueInventoryImageUrl(inventoryItem, customImageUrl);

  return basicInventoryItem as IBasicInventory;
};
